/**
 * 이상한 방 - 카탈로그 엔트리: 주방·욕실 설비
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 4.1절(그리드 해상도) · 4.2절(무변화 회전) · 8절(카탈로그 규격)
 * 계약: `src/catalog/entryTypes.ts` · 매니페스트: `public/assets/models.json`
 *
 * ## 이 파일이 존재하는 이유
 *
 * 욕실 구획은 소품 풀이 `trash-bin` 하나뿐이라 **71개 중 62개가 휴지통**이었다.
 * 배치기를 고쳐도 없는 소품은 놓을 수 없으므로 하한은 카탈로그 데이터가 져야 한다.
 * 주방도 사정이 같아 `kitchen-cabinet`·`kitchen-stove`·`fridge` 세 종이 구획 전체를 채우고 있었다.
 *
 * ## 값을 어떻게 정했는가 - 셋 다 추정하지 않았다
 *
 * 1. **footprint**: `cellsFromMeters(models.json[modelId].sizeMeters)` 의 결과를 그대로 적었다.
 *    각 줄 주석의 `sizeMeters` 가 그 근거다. 손으로 다시 곱하지 않았다.
 * 2. **rotSymmetry**: 미리보기(`catalog-preview.png`)로 형태를 확인한 뒤,
 *    앞뒤 대칭 여부는 눈으로 확정할 수 없으므로 **GLB 정점을 180°/90° 돌려 원본 점군과 대조해** 실측했다.
 *    그 결과 `kitchen-bar` 만 180° 무변화(= 2)였고 나머지는 전부 비대칭(= 1)이다.
 *    앞뒤가 같은 물체에 1을 선언하면 서버는 회전을 승인하는데 화면은 그대로다 - 원칙 2 위반이다.
 * 3. **recolorSlot**: GLB의 머티리얼별 **삼각형 표면적 비중**을 재서 지배적인 색면을 골랐다.
 *    매니페스트의 `materials` 배열 순서는 면적 순이 아니라서, 첫 항목을 고르면 후드처럼
 *    18%짜리 안쪽 면만 칠해지는 일이 생긴다.
 *
 * ## 채택하지 않은 모델과 이유
 *
 * - `kitchen-stove` · `kitchen-fridge` · `kitchen-microwave` · `toilet` · `bathtub` ·
 *   `bathroom-sink` · `bathroom-mirror` - **이미 기존 44종이 쓰고 있는 GLB다.**
 *   같은 modelId를 다시 배선하면 한 물건이 두 이름으로 나타난다(실루엣 중복의 정의 그 자체).
 * - `kitchen-stove-electric` - 이미 배선된 `kitchen-stove` 와 몸통·오븐문·크기가 동일하고
 *   **상판 화구 모양만** 다르다(가스 그레이트 ↔ 평면 하이라이트). 외곽선이 같으므로 제외.
 * - `toilet-square` - 기존 `toilet` 과 footprint(3×4×4)까지 같고 변기통 모서리 곡률만 다르다.
 *   욕실이 모자란 것은 **설비 종수가 아니라 소품 수**이므로 여기서 채울 문제가 아니다.
 *
 * ## 통합 담당에게 남기는 메모
 *
 * 이 그룹은 전부 대형 설비라 `catalog.test.ts` 의 "소품 풀 4종" 기준
 * (바닥면적 ≤ 4 · 높이 ≤ 4)을 만족하는 항목이 하나도 없다. 욕실 소품 하한은
 * 수건·비누·휴지 같은 **소형 그룹**이 채워야 한다.
 * 또한 실측 footprint가 서로 겹치는 조합이 있다(4×4×4 · 5×9×5 · 2×2×1).
 * `cellsFromMeters` 결과를 손으로 흔들 수는 없으므로, 기존의 footprint 유일성 테스트는
 * 계약이 옮겨 간 대로 `assertEntryInvariants` 의 **modelId 유일성**으로 대체돼야 한다.
 */

import type { CatalogEntryDraft } from '../entryTypes'

/* ------------------------------------------------------------------------------------------------
 * 표에서 반복되는 숫자의 의미
 * ---------------------------------------------------------------------------------------------- */

/**
 * 배치 빈도 등급. 숫자를 각 줄에 직접 적으면 17줄을 오가는 사이에 기준이 흔들린다
 * - "냉장고 3"과 "샤워부스 5"가 무엇을 뜻하는지 표만 보고는 복원할 수 없다.
 * 밀도는 종 수가 아니라 이 분포에서 나오므로(entryTypes 주석) 등급을 이름으로 고정한다.
 */
const FREQ = {
  /** 그 구획이라면 대체로 하나는 있는 설비(싱크대). */
  essential: 8,
  /** 자주 보이는 가전(토스터·세면대). */
  frequent: 6,
  /** 흔하지만 없어도 이상하지 않은 것(세탁기·믹서기). */
  common: 5,
  /** 집집마다 갈리는 것(건조기·소형 냉장고). */
  uncommon: 4,
  /** 한 집에 하나 있을까 말까 한 것(양문형 냉장고·후드). */
  occasional: 3,
  /** 구조상 짝이 있어야만 성립하는 것(바 카운터 마감재). */
  rare: 2,
} as const

/**
 * 기본색. 팔레트(`palette.ts`) 10색 중 그 물건의 **재질에 가까운 값**이며,
 * 배치기가 색을 흩뿌리기 전의 출발점일 뿐 게임 규칙상의 의미는 없다.
 */
const COLOR = {
  /** 0 코코아 - 짙은 금속(후드 캐노피·커피머신). */
  charcoal: 0,
  /** 2 먹청록 - 색이 들어간 소형가전. */
  teal: 2,
  /** 5 카페라떼 - 기본 원목(하부장 몸통). */
  wood: 5,
  /** 8 살구크림 - 스테인리스 계열 밝은 금속 패널. */
  steel: 8,
  /** 9 우유빛 - 도자기·백색가전. */
  porcelain: 9,
} as const

/* ------------------------------------------------------------------------------------------------
 * 주방 설비
 * ---------------------------------------------------------------------------------------------- */

const KITCHEN_ENTRIES: readonly CatalogEntryDraft[] = [
  {
    // sizeMeters [0.86, 0.98, 0.9] → 4×4×4. 하부장 위에 개수대와 수도꼭지가 얹힌 한 덩어리다.
    // rot 1: 수도꼭지가 뒤쪽 한 변에만 붙어 네 방향이 전부 구별된다(실측 r180 0.03).
    // anchor: 배수 때문에 벽을 등지는 물건이고, 카운터 상판이 시선 높이 아래 면을 만든다.
    // recolor 'wood'(76%): 상판 metal(22%)까지 칠하면 개수대가 몸통에 묻혀 실루엣이 뭉개진다.
    id: 'kitchen-sink',
    modelId: 'kitchen-sink',
    name: '싱크대',
    footprint: { x: 4, y: 4, z: 4 },
    rotSymmetry: 1,
    sizeClass: 'anchor',
    recolorSlot: 'wood',
    defaultColorId: COLOR.wood,
    tags: ['kitchen'],
    weight: FREQ.essential,
  },
  {
    // sizeMeters [0.86, 1.2, 0.584] → 4×5×3. 기존 `fridge`(4×8×3)의 절반 높이라 실루엣이 갈린다.
    // rot 1: 문짝과 손잡이가 한쪽 면에만 있다.
    // floor: 1.2m라 시야를 막지 못한다. 벽 슬롯을 큰 설비에 양보하고 바닥에 자유롭게 선다.
    id: 'fridge-small',
    modelId: 'kitchen-fridge-small',
    name: '소형 냉장고',
    footprint: { x: 4, y: 5, z: 3 },
    rotSymmetry: 1,
    sizeClass: 'floor',
    recolorSlot: 'metalLight',
    defaultColorId: COLOR.porcelain,
    // 원룸·침실의 미니 냉장고는 실제로 흔하다. 주방 밖으로 새는 것이 오히려 정상이다.
    tags: ['kitchen', 'bedroom', 'living'],
    weight: FREQ.uncommon,
  },
  {
    // sizeMeters [1.04, 1.84, 0.811] → 5×8×4. 기존 `fridge`와 높이는 같지만 폭·깊이가 한 칸씩 넓고
    // 정면 중앙에 양문 분할선이 있어 가까이서든 멀리서든 구별된다.
    // recolor 'metalLight'(99%): 사실상 전체가 한 색면인 물건이라 다른 선택지가 없다.
    id: 'fridge-large',
    modelId: 'kitchen-fridge-large',
    name: '양문형 냉장고',
    footprint: { x: 5, y: 8, z: 4 },
    rotSymmetry: 1,
    sizeClass: 'anchor',
    recolorSlot: 'metalLight',
    defaultColorId: COLOR.steel,
    tags: ['kitchen'],
    weight: FREQ.occasional,
  },
  {
    // sizeMeters [0.86, 1.737, 0.9] → 4×7×4. 목재 전면 패널이라 금속 냉장고 두 종과 색·재질이 다르다.
    // recolor 'wood'(47%): 몸통 metalLight(47%)를 남겨 두면 "나무 문짝 + 금속 몸체" 구조가 살아난다.
    id: 'fridge-built-in',
    modelId: 'kitchen-fridge-built-in',
    name: '빌트인 냉장고',
    footprint: { x: 4, y: 7, z: 4 },
    rotSymmetry: 1,
    sizeClass: 'anchor',
    recolorSlot: 'wood',
    defaultColorId: COLOR.wood,
    tags: ['kitchen'],
    weight: FREQ.occasional,
  },
  {
    // sizeMeters [0.86, 0.84, 0.42] → 4×4×2.
    // rot 2: **실측값이 정확히 1.000** - 앞뒤 패널이 동일해 180° 회전은 화면상 무변화다.
    // 여기에 1을 적었다면 술래가 찾을 수 없는 정답이 만들어졌다.
    // floor: 벽을 등지는 물건이 아니라 방 가운데 서는 아일랜드 카운터다.
    id: 'kitchen-bar',
    modelId: 'kitchen-bar',
    name: '바 카운터',
    footprint: { x: 4, y: 4, z: 2 },
    rotSymmetry: 2,
    sizeClass: 'floor',
    recolorSlot: 'wood',
    defaultColorId: COLOR.wood,
    tags: ['kitchen', 'living'],
    weight: FREQ.common,
  },
  {
    // sizeMeters [0.203, 0.84, 0.42] → 1×4×2. 바 카운터의 반원 마감재.
    // rot 1: 한쪽은 평면(카운터와 붙는 면), 반대쪽은 반원이다. 좌우 거울 대칭일 뿐 회전 대칭은 없다.
    // 폭 1칸짜리 세로 기둥이라 실루엣이 다른 어떤 주방 설비와도 겹치지 않는다.
    id: 'kitchen-bar-end',
    modelId: 'kitchen-bar-end',
    name: '반원 바카운터',
    footprint: { x: 1, y: 4, z: 2 },
    rotSymmetry: 1,
    sizeClass: 'floor',
    recolorSlot: 'wood',
    defaultColorId: COLOR.wood,
    tags: ['kitchen', 'living'],
    weight: FREQ.rare,
  },
  {
    // sizeMeters [0.86, 0.739, 0.57] → 4×3×3. 아래로 넓게 벌어진 사다리꼴 캐노피 + 각진 굴뚝.
    // wall: 조리대 위 허공에 매달리는 물건이라 바닥 등급으로 두면 배치기가 세워 버린다.
    // recolor 'metalDark'(82%): _defaultMat(18%)은 캐노피 안쪽이라 밖에서 거의 보이지 않는다.
    id: 'range-hood',
    modelId: 'hood-large',
    name: '대형 레인지후드',
    footprint: { x: 4, y: 3, z: 3 },
    rotSymmetry: 1,
    sizeClass: 'wall',
    recolorSlot: 'metalDark',
    defaultColorId: COLOR.charcoal,
    tags: ['kitchen'],
    weight: FREQ.occasional,
  },
  {
    // sizeMeters [0.86, 0.799, 0.57] → 4×4×3. 위와 달리 **평평한 판 + 위로 좁아지는 쐐기 굴뚝**이라
    // 정면 실루엣이 확연히 다르다(벌어지느냐 곧게 서느냐). 그래서 두 종을 모두 채택했다.
    id: 'range-hood-modern',
    modelId: 'hood-modern',
    name: '모던 레인지후드',
    footprint: { x: 4, y: 4, z: 3 },
    rotSymmetry: 1,
    sizeClass: 'wall',
    recolorSlot: 'metalMedium',
    defaultColorId: COLOR.steel,
    tags: ['kitchen'],
    weight: FREQ.occasional,
  },
  {
    // sizeMeters [0.376, 0.26, 0.2] → 2×2×1. 상판의 빵 투입구와 정면 레버가 방향을 만든다 → rot 1.
    // surface: 조리대·식탁 위에 올라간다. 바닥에 놓이면 크기상 보이지 않는다.
    id: 'toaster',
    modelId: 'toaster',
    name: '토스터',
    footprint: { x: 2, y: 2, z: 1 },
    rotSymmetry: 1,
    sizeClass: 'surface',
    recolorSlot: 'metal',
    defaultColorId: COLOR.porcelain,
    tags: ['kitchen'],
    weight: FREQ.frequent,
  },
  {
    // sizeMeters [0.272, 0.455, 0.218] → 2×2×1. 토스터와 점유 칸은 같지만 세로로 선 원뿔대라
    // 실루엣은 정반대다(눕힌 상자 ↔ 세운 병 모양).
    // recolor 'metalMedium'(30%): 면적 1위는 유리 용기(70%)지만 그건 색면이 아니라 투과체다.
    // 유리를 칠하면 믹서기가 아니라 색 덩어리가 된다 - 몸통(모터부)이 이 물건의 색이다.
    id: 'blender',
    modelId: 'kitchen-blender',
    name: '믹서기',
    footprint: { x: 2, y: 2, z: 1 },
    rotSymmetry: 1,
    sizeClass: 'surface',
    recolorSlot: 'metalMedium',
    defaultColorId: COLOR.teal,
    tags: ['kitchen'],
    weight: FREQ.common,
  },
  {
    // sizeMeters [0.379, 0.355, 0.48] → 2×2×2. 추출구가 한쪽으로만 튀어나와 네 방향이 전부 다르다.
    id: 'coffee-machine',
    modelId: 'kitchen-coffee-machine',
    name: '커피머신',
    footprint: { x: 2, y: 2, z: 2 },
    rotSymmetry: 1,
    sizeClass: 'surface',
    recolorSlot: 'metalMedium',
    defaultColorId: COLOR.charcoal,
    // 사무실 책상·거실 선반 위에도 놓인다. 주방 밖 한 종을 늘리는 값싼 방법이다.
    tags: ['kitchen', 'living'],
    weight: FREQ.common,
  },
]

/* ------------------------------------------------------------------------------------------------
 * 욕실·세탁 설비
 *
 * 세탁기 3종에 `hall` 을 함께 준 이유: 세탁기가 욕실 안이 아니라 복도 끝 다용도실에 있는 집이 흔하고,
 * 무엇보다 **복도가 거의 비어 있는 것이 현재의 문제**다. 태그는 금지가 아니라 배치 힌트이므로
 * 여기서 인색하게 굴 이유가 없다.
 * ---------------------------------------------------------------------------------------------- */

const BATH_ENTRIES: readonly CatalogEntryDraft[] = [
  {
    // sizeMeters [0.78, 0.94, 0.78] → 4×4×4. 정면의 큰 원형 도어가 이 물건의 표식이다 → rot 1.
    // floor: 0.94m라 시야를 막지 않는다. 벽 슬롯을 샤워부스·수납장에 양보한다.
    id: 'washer',
    modelId: 'washer',
    name: '세탁기',
    footprint: { x: 4, y: 4, z: 4 },
    rotSymmetry: 1,
    sizeClass: 'floor',
    recolorSlot: 'metalLight',
    defaultColorId: COLOR.porcelain,
    tags: ['bath', 'hall', 'kitchen'],
    weight: FREQ.common,
  },
  {
    // sizeMeters [0.78, 0.94, 0.76] → 4×4×4. 세탁기와 몸통은 같지만 **정면이 원형 도어가 아니라
    // 평평한 문짝 + 측면 손잡이**다. 정면 한 면 전체가 달라 1인칭 시야에서 바로 갈린다.
    // 세탁기보다 낮은 가중치를 준 이유: 건조기가 없는 집이 있는 집보다 많다.
    id: 'dryer',
    modelId: 'dryer',
    name: '건조기',
    footprint: { x: 4, y: 4, z: 4 },
    rotSymmetry: 1,
    sizeClass: 'floor',
    recolorSlot: 'metalLight',
    defaultColorId: COLOR.steel,
    tags: ['bath', 'hall'],
    weight: FREQ.uncommon,
  },
  {
    // sizeMeters [0.78, 1.88, 0.78] → 4×8×4. 두 대를 쌓은 1.88m 기둥이라 **시야 차단물**이 된다.
    // 그래서 같은 세탁 가전이지만 등급이 anchor로 갈린다.
    id: 'washer-dryer-tower',
    modelId: 'washer-dryer-stacked',
    name: '세탁건조기 타워',
    footprint: { x: 4, y: 8, z: 4 },
    rotSymmetry: 1,
    sizeClass: 'anchor',
    recolorSlot: 'metalLight',
    defaultColorId: COLOR.porcelain,
    tags: ['bath', 'hall'],
    weight: FREQ.occasional,
  },
  {
    // sizeMeters [1.124, 2.188, 1.164] → 5×9×5. 각진 부스에 유리문이 한 면에만 있다 → rot 1.
    // 카탈로그에서 가장 높은 소품 축에 든다(2.19m) - 욕실 안에서 확실한 은폐물이 된다.
    // recolor 'carpetWhite'(58%): 유리(42%)는 남겨 둬야 부스 안이 비쳐 보이는 형태가 유지된다.
    id: 'shower-booth',
    modelId: 'shower',
    name: '샤워부스',
    footprint: { x: 5, y: 9, z: 5 },
    rotSymmetry: 1,
    sizeClass: 'anchor',
    recolorSlot: 'carpetWhite',
    defaultColorId: COLOR.porcelain,
    tags: ['bath'],
    weight: FREQ.common,
  },
  {
    // sizeMeters [1.124, 2.188, 1.124] → 5×9×5. 점유 칸은 위와 같지만 단면이 **사분원**이다 -
    // 두 직각 벽 + 곡면 전면이라 각진 부스와 외곽선이 겹치지 않는다.
    // 정사각 footprint지만 rot 4가 아니다: 곡면이 한쪽에만 있어 90° 회전이 전부 보인다(실측 r90 0.08).
    id: 'shower-booth-round',
    modelId: 'shower-round',
    name: '곡면 샤워부스',
    footprint: { x: 5, y: 9, z: 5 },
    rotSymmetry: 1,
    sizeClass: 'anchor',
    recolorSlot: '_defaultMat',
    defaultColorId: COLOR.steel,
    tags: ['bath'],
    weight: FREQ.uncommon,
  },
  {
    // sizeMeters [0.86, 1.16, 0.6] → 4×5×3. 기존 `bathroom-sink`(둥근 받침 + 타원 볼, 3×5×3)와 달리
    // **직육면체 볼 + 각진 받침**이라 단면이 다르다. 수도꼭지가 뒤쪽 한 변에만 있어 rot 1.
    // recolor 'carpetWhite'(96%): 도기 전체가 한 색면인 물건이다.
    id: 'bathroom-sink-square',
    modelId: 'bathroom-sink-square',
    name: '사각 세면대',
    footprint: { x: 4, y: 5, z: 3 },
    rotSymmetry: 1,
    sizeClass: 'anchor',
    recolorSlot: 'carpetWhite',
    defaultColorId: COLOR.porcelain,
    tags: ['bath'],
    weight: FREQ.frequent,
  },
]

/**
 * 주방·욕실 그룹 17종. 통합 담당은 **이 심볼 하나만** import한다.
 *
 * 내부를 두 배열로 나눈 것은 읽기 편의를 위한 것일 뿐이며,
 * 밖에서 보이는 계약은 이 배열 하나다(그래야 병합 코드가 그룹 수만큼 늘지 않는다).
 */
export const KITCHEN_BATH_ENTRIES: readonly CatalogEntryDraft[] = [
  ...KITCHEN_ENTRIES,
  ...BATH_ENTRIES,
]
